/// <amd-module name="Test/TestSummary/Sources/TestResultSource" />
import * as Common from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test"
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageContext } from "VSS/Platform/Context";

export class TestResultSource {
    private _pageContext: IVssPageContext;
    private readonly _metricsAPIinunifiedPipeline: string = "WebAccess.TestManagement.UnifiedPipelineMetricsAPI";
    public constructor(_context: IVssComponentContext){
        this._pageContext = _context.pageContext;
    }

    public getTestReportFromDataProviderAsync(artifact: Common.IViewContextData, context: IVssComponentContext): Promise<TCMContracts.TestResultSummary> {
        return new Promise<TCMContracts.TestResultSummary> (async (resolve, reject)=> {
            try {
                const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");

                if (artifact.status === Common.ViewContextStatus.InProgress) {
                    const inProgressReports = await contributionService.getDataAsync(this._getDataProviderName(artifact), this._inProgressDPRequestParams, true) as TCMContracts.TestResultSummary;
                    return resolve(inProgressReports);
                }

                const reports = contributionService.getData(this._getDataProviderName(artifact)) as TCMContracts.TestResultSummary;
                if (this._isDataProviderCacheValid(contributionService, reports, artifact)) {
                    return resolve(reports);
                }

                return resolve(await this._refreshAndGetDataProviderCache(contributionService, artifact));
            } catch (error) {
                reject(error);
            }
        });
    }

    public getTestReportFromDataProviderForUnifiedPipelineAsync(artifact: Common.IViewContextData, context: IVssComponentContext): Promise<TCMContracts.PipelineTestMetrics> {
        return new Promise<TCMContracts.PipelineTestMetrics> (async (resolve, reject)=> {
            try {
                const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");

                if (artifact.status === Common.ViewContextStatus.InProgress) {
                    const inProgressReports = await contributionService.getDataAsync(this.pipelineSummaryDataProviderName, this._inProgressDPRequestParams, true) as TCMContracts.PipelineTestMetrics;
                    return resolve(inProgressReports);
                }

                const reports = contributionService.getData(this.pipelineSummaryDataProviderName) as TCMContracts.PipelineTestMetrics;
                if (this._isDataProviderCacheValidForUnifiedPipeline(contributionService, reports, artifact)) {
                    return resolve(reports);
                }

                return resolve(await this._refreshAndGetDataProviderCacheForUnifiedPipeline(contributionService, artifact));
            } catch (error) {
                reject(error);
            }
        });
    }

    private _isDataProviderCacheValid(contributionService: IVssContributionService,reports: TCMContracts.TestResultSummary, artifact: Common.IViewContextData): boolean {
        if (!reports) {
            return false;
        }

        if (artifact.viewContext === Common.ViewContext.Build && reports.testResultsContext.build.id !== artifact.data.mainData.id) {
            return false;
        }
        
        if (artifact.viewContext === Common.ViewContext.Release && 
            (reports.testResultsContext.release.id !== artifact.data.mainData.id ||
            reports.testResultsContext.release.environmentId !== artifact.data.subData.environment.id) 
        ) {
            return false;
        }

        // Making data provider cache invalid when you have inprogress data provider cache in completed state.
        return contributionService.getData(this._getDataProviderName(artifact), this._inProgressDPRequestParams) ? false : true;
    }

    private _isDataProviderCacheValidForUnifiedPipeline(contributionService: IVssContributionService, reports: TCMContracts.PipelineTestMetrics, artifact: Common.IViewContextData): boolean {
        if (!reports) {
            return false;
        }

        if (artifact.viewContext === Common.ViewContext.Build && 
            (reports.currentContext.pipelineId !== artifact.data.mainData.id) || (artifact.contributionSource !== Common.ContributionSource.Pipeline)) {
            return false;
        }
        
        // Making data provider cache invalid when you have inprogress data provider cache in completed state.
        return contributionService.getData(this.pipelineSummaryDataProviderName, this._inProgressDPRequestParams) ? false : true;
    }

    private async _refreshAndGetDataProviderCache(contributionService: IVssContributionService, artifact: Common.IViewContextData): Promise<TCMContracts.TestResultSummary> {
        const refreshedTestSummary = contributionService.getData(this._getDataProviderName(artifact), this._completedDPRequestParams) as TCMContracts.TestResultSummary;

        // Not refreshing the summary if it's already refreshed in completed state.
        const testSummary = this._isDataProviderCacheValid(contributionService, refreshedTestSummary, artifact) ? refreshedTestSummary as TCMContracts.TestResultSummary : await contributionService.getDataAsync(this._getDataProviderName(artifact), this._completedDPRequestParams, true) as TCMContracts.TestResultSummary;
        return testSummary;
    }

    private async _refreshAndGetDataProviderCacheForUnifiedPipeline(contributionService: IVssContributionService, artifact: Common.IViewContextData): Promise<TCMContracts.PipelineTestMetrics> {
        const refreshedTestSummary = contributionService.getData(this.pipelineSummaryDataProviderName, this._completedDPRequestParams) as TCMContracts.PipelineTestMetrics;

        // Not refreshing the summary if it's already refreshed in completed state.
        const testSummary = this._isDataProviderCacheValidForUnifiedPipeline(contributionService, refreshedTestSummary, artifact) ? 
                            refreshedTestSummary as TCMContracts.PipelineTestMetrics : 
                            await contributionService.getDataAsync(this.pipelineSummaryDataProviderName, this._completedDPRequestParams, true) as TCMContracts.PipelineTestMetrics;
        return testSummary;
    }

    private _getDataProviderName(artifact: Common.IViewContextData): string
    {
        if (artifact.viewContext === Common.ViewContext.Build)
        {
            return this.buildSummaryDataProviderName;
        } 
        else
        {
            return this.releaseSummaryDataProviderName;
        }
    }

    private readonly _inProgressDPRequestParams = {"state": "InProgress"};
    private readonly _completedDPRequestParams = {"state": "Completed"};
    private readonly buildSummaryDataProviderName = "ms.vss-test-web.test-tab-build-summary-data-provider";
    private readonly pipelineSummaryDataProviderName = "ms.vss-test-web.test-tab-unifiedPipeline-summary-data-provider";
    private readonly releaseSummaryDataProviderName = "ms.vss-test-web.test-tab-release-summary-data-provider";
}
